import Section from "../Section";
import { useEffect, useState } from "react";
import { useApiData } from "../../contexts/ApiData";
import { H1 } from "../Headers/Headings";
import SystemMessage from "../SystemMessage/Index";
import EmScore from "../Survey/EmScore";
import { Department, Stats } from "../../types";
import { useTranslations } from "../../contexts/Translation";
import Input, { Select } from "../Forms/Input";
import { InfoMessageBox } from "../InfoMessage/InfoMessageBox";
import { InfoItem } from "../Survey/InfoItem";
import { sub, addDays } from "date-fns";
import Button from "../Button/Button";
import { CreateImprovementGoalLink } from "./CreateImprovementGoalLink";
import ImprovementGoals from "../ImprovementGoal/List";
import { useOrganizations, useRole } from "../../contexts/UserProvider";
import AnswersForSurveyType from "./AnswersForSurveyType";
import styles from "./Index.module.css";

const Dashboard = () => {
  const { get } = useApiData();
  const { currentOrganization, organizations } = useOrganizations();
  const [stats, setStats] = useState<Stats | null>(null);
  const [periodValue, setPeriod] = useState("forever");
  const [from, setFrom] = useState(sub(new Date(), { months: 6 }).toISOString().substring(0, 10));
  const [to, setTo] = useState(new Date().toISOString().substring(0, 10));
  const t = useTranslations("orgDash");
  const role = useRole();

  const [departments, setDepartments] = useState<Department[]>([]);
  const [department, setDepartment] = useState<string>("");
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(true);

  const getDateInterval = () => {
    const now = new Date();
    let fromDate = "2000-01-01",
      toDate = addDays(now, 365).toISOString().substring(0, 10);
    if (periodValue === "interval") {
      fromDate = from;
      toDate = to;
    }
    if (periodValue === "12months") {
      fromDate = sub(now, { years: 1 }).toISOString().substring(0, 10);
    }
    if (periodValue === "6months") {
      fromDate = sub(now, { months: 6 }).toISOString().substring(0, 10);
    }
    if (periodValue === "3months") {
      fromDate = sub(now, { months: 3 }).toISOString().substring(0, 10);
    }
    // default: periodValue === 'forever') {

    return { fromDate, toDate };
  };

  const getStats = async () => {
    const { fromDate, toDate } = getDateInterval();
    const response = await get(`/organization/stats?fromDate=${fromDate}&toDate=${toDate}&department=${department}`);
    setStats(response);
  };

  useEffect(() => {
    getStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const call = async () => {
      const response = await get(`/organization/${currentOrganization}/departments`);
      setIsLoadingDepartments(false);
      if (response) {
        const orgName = organizations && organizations[0] ? organizations[0].name : "";
        setDepartments([{ id: null, name: orgName }, ...response]);
        setDepartment("");
      }
    };
    call();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!stats) {
    return <SystemMessage message='loading' />;
  }
  if (isLoadingDepartments) {
    return <SystemMessage message='loading' />;
  }

  return (
    <>
      <Section>
        <H1>{t("scoreHeading")}</H1>
        <div className={styles.filters}>
          <Select
            className={styles.compactField}
            options={[
              { value: "forever", text: t("periodForever") },
              { value: "12months", text: t("periodYear") },
              { value: "6months", text: t("period6m") },
              { value: "3months", text: t("period3m") },
              { value: "interval", text: t("periodInterval") },
            ]}
            handleChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPeriod(e.target.value)}
            name='period'
            label={t("periodLabel")}
            value={periodValue}
          />
          {periodValue === "interval" && (
            <>
              <Input
                className={styles.compactField}
                type='date'
                name='from'
                handleChange={(e: React.ChangeEvent<HTMLInputElement>) => setFrom(e.target.value)}
                label={t("fromDate")}
                value={from}
              />
              <Input
                className={styles.compactField}
                type='date'
                name='to'
                handleChange={(e: React.ChangeEvent<HTMLInputElement>) => setTo(e.target.value)}
                label={t("toDate")}
                value={to}
              />
            </>
          )}
          {departments.length > 0 && (
            <Select
              className={styles.compactField}
              options={departments.map((d) => ({ value: d.id || "", text: d.name }))}
              handleChange={(e: React.ChangeEvent<HTMLSelectElement>) => setDepartment(e.target.value)}
              name='department'
              label={t("departmentLabel")}
              value={department}
            />
          )}
          <Button className={styles.updateButton} onClick={() => getStats()}>
            {t("updateBtn")}
          </Button>
        </div>
      </Section>
      <Section span={8} variant='card'>
        <div className={styles.scoreSummary}>
          <div className={styles.totalScore}>
            <EmScore inside={t("card.totalScoreLabel")} score={stats?.total?.totalScore} size={6} />
          </div>
          <div className={styles.scoreInfo}>
            <InfoItem label={t("answersLabel")} value={`${stats?.total?.answers}`} />
            <InfoItem label={t("surveyLabel")} value={`${stats?.total?.surveys}`} />
            <InfoMessageBox body={t("orgScoreInfo")}></InfoMessageBox>
          </div>
        </div>
        <AnswersForSurveyType stats={stats} surveyType='long-term' />
        <AnswersForSurveyType stats={stats} surveyType='workshop' />
        <AnswersForSurveyType stats={stats} surveyType='delivery' />
      </Section>
      <Section span={4} className={styles.goals}>
        {["supporter", "owner"].includes(`${role}`) && <CreateImprovementGoalLink from={from} />}
        <ImprovementGoals isOrgGoals />
      </Section>
    </>
  );
};

export default Dashboard;
